"use client";

import React, { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { handleMediaError, resolveMediaUrl } from "../utils/media";
import { ShoppingCart, Search, Menu, X, LogOut, LayoutDashboard, Home } from "lucide-react";

export const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const router = useRouter();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-black/80 backdrop-blur-md transition-colors duration-300">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          
          {/* Logo & Home Icon */}
          <div className="flex items-center gap-2">
            <Link
              href="/"
              aria-label="Home"
              className="flex items-center justify-center p-2 rounded-full text-zinc-700 dark:text-zinc-300 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-zinc-100 dark:hover:bg-zinc-900/50 transition-all duration-200"
              title="Home"
            >
              <Home className="h-5 w-5" />
            </Link>
            <Link href="/" className="flex items-center gap-2 font-bold text-xl tracking-tight">
              <span className="bg-gradient-to-r from-violet-600 to-indigo-600 dark:from-violet-400 dark:to-indigo-400 text-transparent bg-clip-text font-black">
                TORONGO
              </span>
              <span className="text-zinc-950 dark:text-white">GADGETS</span>
            </Link>
          </div>

          {/* Search Bar - Hidden on Mobile */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md relative">
            <input
              type="text"
              placeholder="Search premium gadgets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-4 pr-10 py-1.5 rounded-full border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 text-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
            />
            <button type="submit" aria-label="Submit Search" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200">
              <Search className="h-4 w-4" />
            </button>
          </form>

          {/* Desktop Right Menu */}
          <nav className="hidden md:flex items-center gap-6">
            <Link href="/shop" className="text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors">
              Shop
            </Link>
            
            {/* Cart Icon */}
            <Link href="/cart" aria-label="Shopping Cart" className="relative p-2 text-zinc-700 dark:text-zinc-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors">
              <ShoppingCart className="h-5.5 w-5.5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-violet-600 text-[10px] font-bold text-white ring-2 ring-white dark:ring-black">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* Auth Actions */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  aria-label="User Account Menu"
                  className="flex items-center gap-2 p-1 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors focus:outline-none"
                >
                  {user.profileImage ? (
                    <img
                      src={resolveMediaUrl(user.profileImage)}
                      alt={user.name}
                      onError={handleMediaError}
                      className="h-8 w-8 rounded-full object-cover ring-2 ring-violet-500"
                    />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-100 dark:bg-violet-950 text-violet-700 dark:text-violet-300 font-semibold ring-2 ring-violet-500">
                      {user.name[0].toUpperCase()}
                    </div>
                  )}
                </button>

                {userDropdownOpen && (
                  <>
                    {/* Overlay to close on outside click */}
                    <div className="fixed inset-0 z-10" onClick={() => setUserDropdownOpen(false)} />
                    <div className="absolute right-0 mt-2 w-56 origin-top-right rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 shadow-lg ring-1 ring-black/5 focus:outline-none z-20 overflow-hidden divide-y divide-zinc-100 dark:divide-zinc-900">
                      <Link
                        href="/dashboard"
                        onClick={() => setUserDropdownOpen(false)}
                        className="block px-4 py-3 transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900"
                      >
                        <p className="text-xs text-zinc-400">Signed in as</p>
                        <p className="truncate text-sm font-semibold text-zinc-900 dark:text-white">{user.name}</p>
                        <p className="truncate text-xs text-zinc-500">{user.email}</p>
                        <span className="mt-1 inline-flex items-center rounded-full bg-violet-50 dark:bg-violet-900/50 px-2 py-0.5 text-xs font-medium text-violet-700 dark:text-violet-300">
                          {user.role}
                        </span>
                      </Link>
                      
                      <div className="py-1">
                        <Link
                          href="/dashboard"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
                        >
                          <LayoutDashboard className="h-4 w-4 text-zinc-400" />
                          Dashboard
                        </Link>
                      </div>

                      <div className="py-1">
                        <button
                          onClick={() => {
                            setUserDropdownOpen(false);
                            logout();
                            router.push("/");
                          }}
                          className="flex w-full items-center gap-2 px-4 py-2 text-sm text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/20 transition-colors"
                        >
                          <LogOut className="h-4 w-4" />
                          Logout
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link
                href="/login"
                className="inline-flex items-center justify-center rounded-full bg-zinc-950 dark:bg-zinc-100 px-4 py-1.5 text-xs font-semibold text-white dark:text-zinc-950 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors"
              >
                Login
              </Link>
            )}
          </nav>

          {/* Mobile Menu Actions */}
          <div className="flex md:hidden items-center gap-3">
            {/* Mobile Cart */}
            <Link href="/cart" className="relative p-2 text-zinc-700 dark:text-zinc-300">
              <ShoppingCart className="h-5.5 w-5.5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-violet-600 text-[10px] font-bold text-white">
                  {itemCount}
                </span>
              )}
            </Link>
            
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle Navigation Menu"
              className="p-2 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-md focus:outline-none"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 px-4 py-4 space-y-4">
          <form onSubmit={handleSearch} className="relative w-full">
            <input
              type="text"
              placeholder="Search premium gadgets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-4 pr-10 py-2 rounded-full border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 text-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none"
            />
            <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400">
              <Search className="h-4 w-4" />
            </button>
          </form>

          <div className="flex flex-col gap-3 font-medium text-sm">
            <Link
              href="/shop"
              onClick={() => setMobileMenuOpen(false)}
              className="py-2 text-zinc-700 dark:text-zinc-300 hover:text-violet-600 dark:hover:text-violet-400"
            >
              Shop All Products
            </Link>

            {user ? (
              <>
                <Link
                  href="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block border-t border-zinc-100 pt-3 dark:border-zinc-900"
                >
                  <p className="text-xs text-zinc-400">Logged in as {user.name}</p>
                  <p className="mt-1 truncate text-xs text-zinc-500">{user.email}</p>
                </Link>
                <Link
                  href="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-2 flex items-center gap-2 text-zinc-700 dark:text-zinc-300"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  Dashboard
                </Link>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                    router.push("/");
                  }}
                  className="py-2 flex items-center gap-2 text-rose-600 dark:text-rose-400 font-semibold"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </>
            ) : (
              <Link
                href="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="mt-2 text-center rounded-full bg-zinc-950 dark:bg-zinc-100 py-2 text-sm font-semibold text-white dark:text-zinc-950 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors"
              >
                Login / Account Creation
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
export default Navbar;
